import { useCallback } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { resolveDatabase } from '../../common/utils/resolveDatabase';

type UseConnectionSwitchParams = {
  tabId: string;
  /** Results-pane tab setter — snaps a now-orphaned `pinned-*` tab back to 'results'. */
  setActiveTab: (updater: (prev: string) => string) => void;
};

/**
 * Status-bar connection switch for a query editor tab: repoints the tab's
 * connectionInfo, discards live result sources (TempSqlite files, STS
 * ownerUri refs) and pinned-result backing sources, clears/regenerates the
 * result cache, and keeps the same-named database when the new connection has
 * one. Behavior carried verbatim from the previous inline implementation.
 */
export function useConnectionSwitch({ tabId, setActiveTab }: UseConnectionSwitchParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const updateTabConnectionInfo = useWorkspaceStore((state) => state.updateTabConnectionInfo);

  const handleConnectionChange = useCallback(
    async (newConnectionId: string, newConnectionName: string, newServerHost: string) => {
      const currentTabState = useWorkspaceStore.getState().getTab(tabId);
      const currentDatabase = currentTabState?.connectionInfo?.database;

      updateTabConnectionInfo(tabId, {
        connectionId: newConnectionId,
        connectionName: newConnectionName,
        serverHost: newServerHost,
        database: undefined,
      });

      // Discard live result sources (TempSqlite files, STS ownerUri refs)
      const cacheUris =
        currentTabState?.type === 'query' ? currentTabState.content?.resultSetCacheUris ?? [] : [];
      for (const uri of cacheUris) {
        if (uri) {
          window.electronAPI?.resultCache.discard({ uri }).catch(() => {});
        }
      }

      // Discard backing sources for pinned results before clearing, mirroring handleClosePinnedTab.
      const existingPinned =
        currentTabState?.type === 'query' ? currentTabState.content?.pinnedResultSets ?? [] : [];
      for (const pinned of existingPinned) {
        for (const uri of pinned.sourceUris ?? []) {
          if (uri) window.electronAPI?.resultCache.discard({ uri }).catch(() => {});
        }
      }

      const currentCacheGen =
        currentTabState?.type === 'query' ? currentTabState.content?.cacheGeneration ?? 0 : 0;
      updateTabContent(tabId, {
        resultSets: undefined,
        pinnedResultSets: undefined,
        resultSetCacheUris: undefined,
        cacheGeneration: currentCacheGen + 1,
      });

      // If the active tab pointed at a now-removed pinned entry, snap back to the live results tab
      setActiveTab((prev) => (prev.startsWith('pinned-') ? 'results' : prev));

      // If the new connection has a database with the same name, keep it
      const resolved = await resolveDatabase(newConnectionId, currentDatabase);
      if (resolved) {
        updateTabConnectionInfo(tabId, { database: resolved });
      }
    },
    [tabId, updateTabConnectionInfo, updateTabContent]
  );

  return { handleConnectionChange };
}
